import { useEffect, useState, type FormEvent, type JSX } from 'react';

import { shaktiApi, ApiError } from '../api/client';
import { useProjectStore } from '../stores/project';

/**
 * H3.3 (round-4) + K4.4 (round-5) — settings page with Providers,
 * API keys, and VCS credentials panels.
 *
 * PM/PO surface for:
 *   - Connecting BYOK LLM providers (Anthropic, OpenAI, Google, etc.)
 *   - Minting Bearer tokens for API / CLI use
 *   - Storing GitHub/GitLab PATs for push + PR automation
 */
type SettingsTab = 'providers' | 'api-keys' | 'vcs' | 'scim' | 'saml' | 'dsar';

export function SettingsRoute(): JSX.Element {
  const [tab, setTab] = useState<SettingsTab>('providers');

  return (
    <section aria-labelledby="settings-title" className="mx-auto flex max-w-5xl flex-col gap-6">
      <header>
        <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
          /dashboard/settings
        </span>
        <h1 id="settings-title" className="text-2xl font-semibold text-fg-primary">
          Settings
        </h1>
        <p className="text-sm text-fg-secondary">
          Manage BYOK LLM providers, API keys, VCS credentials, SCIM provisioning tokens,
          SAML SSO metadata, and GDPR DSAR operations for this organisation.
        </p>
      </header>

      <nav className="flex flex-wrap gap-2" aria-label="Settings tabs">
        <TabButton active={tab === 'providers'} onClick={() => setTab('providers')}>
          LLM providers
        </TabButton>
        <TabButton active={tab === 'api-keys'} onClick={() => setTab('api-keys')}>
          API keys
        </TabButton>
        <TabButton active={tab === 'vcs'} onClick={() => setTab('vcs')}>
          VCS credentials
        </TabButton>
        <TabButton active={tab === 'scim'} onClick={() => setTab('scim')}>
          SCIM
        </TabButton>
        <TabButton active={tab === 'saml'} onClick={() => setTab('saml')}>
          SSO (SAML)
        </TabButton>
        <TabButton active={tab === 'dsar'} onClick={() => setTab('dsar')}>
          DSAR
        </TabButton>
      </nav>

      {tab === 'providers' && <ProvidersPanel />}
      {tab === 'api-keys' && <ApiKeysPanel />}
      {tab === 'vcs' && <VcsCredentialsPanel />}
      {tab === 'scim' && <ScimPanel />}
      {tab === 'saml' && <SamlPanel />}
      {tab === 'dsar' && <DsarPanel />}
    </section>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: JSX.Element | string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-sm px-3 py-1.5 text-xs ${
        active
          ? 'bg-fg-primary text-bg-base'
          : 'border border-border-subtle bg-bg-surface text-fg-secondary'
      }`}
    >
      {children}
    </button>
  );
}

// ── Providers ──────────────────────────────────────────────────────────────

const SUPPORTED_PROVIDERS: ReadonlyArray<{ kind: string; label: string; defaultUrl?: string }> = [
  { kind: 'anthropic', label: 'Anthropic (Claude)' },
  { kind: 'openai', label: 'OpenAI (GPT)' },
  { kind: 'google', label: 'Google (Gemini)' },
  { kind: 'aws_bedrock', label: 'AWS Bedrock' },
  { kind: 'azure_openai', label: 'Azure OpenAI' },
  { kind: 'groq', label: 'Groq' },
  { kind: 'deepseek', label: 'DeepSeek' },
  { kind: 'mistral', label: 'Mistral' },
  { kind: 'ollama', label: 'Ollama (local)', defaultUrl: 'http://localhost:11434' },
];

function ProvidersPanel(): JSX.Element {
  const [registered, setRegistered] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const list = await shaktiApi.listProviders();
      setRegistered(list);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <section className="shakti-card flex flex-col gap-4 p-6">
      <header className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Registered providers</h2>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="rounded-sm bg-fg-primary px-3 py-1.5 text-xs font-semibold text-bg-base"
          data-testid="add-provider-button"
        >
          + Add provider
        </button>
      </header>

      {loading ? (
        <p className="text-xs text-fg-muted">Loading…</p>
      ) : error ? (
        <ErrorBanner message={error} />
      ) : registered.length === 0 ? (
        <p className="text-sm text-fg-secondary">
          No providers connected yet. Add one to unblock the 12-phase SDLC.
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="providers-list">
          {registered.map((kind) => (
            <li
              key={kind}
              className="flex items-center justify-between rounded-sm border border-border-subtle bg-bg-surface px-3 py-2 text-sm"
            >
              <div>
                <span className="font-semibold">{humanProviderName(kind)}</span>
                <span className="ml-2 shakti-mono text-2xs text-fg-muted">{kind}</span>
              </div>
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm(`Remove ${kind}?`)) return;
                  try {
                    await shaktiApi.removeProvider(kind);
                    await refresh();
                  } catch (e) {
                    setError(friendlyError(e));
                  }
                }}
                className="text-xs text-red-300 hover:text-red-200"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {showAdd ? (
        <AddProviderModal
          onClose={() => setShowAdd(false)}
          onAdded={async () => {
            setShowAdd(false);
            await refresh();
          }}
        />
      ) : null}
    </section>
  );
}

function humanProviderName(kind: string): string {
  return SUPPORTED_PROVIDERS.find((p) => p.kind === kind)?.label ?? kind;
}

function AddProviderModal({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  onAdded: () => void;
}): JSX.Element {
  const [kind, setKind] = useState<string>(SUPPORTED_PROVIDERS[0]?.kind ?? 'anthropic');
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const defaultUrl = SUPPORTED_PROVIDERS.find((p) => p.kind === kind)?.defaultUrl ?? '';

  async function submit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await shaktiApi.registerProvider({
        provider: kind,
        api_key: apiKey.trim(),
        base_url: baseUrl.trim() || null,
      });
      onAdded();
    } catch (e) {
      setError(friendlyError(e));
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Connect ${humanProviderName(kind)}`} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-xs">
          Provider
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            disabled={busy}
            data-testid="provider-kind-select"
          >
            {SUPPORTED_PROVIDERS.map((p) => (
              <option key={p.kind} value={p.kind}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          API key
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={kind === 'anthropic' ? 'sk-ant-…' : 'sk-…'}
            className="shakti-mono rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            disabled={busy}
            required
            data-testid="provider-apikey-input"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Base URL (optional)
          <input
            type="url"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder={defaultUrl || 'https://api.provider.example'}
            className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            disabled={busy}
          />
        </label>
        {error ? <ErrorBanner message={error} /> : null}
        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || apiKey.length < 8}
            className="rounded-sm bg-fg-primary px-3 py-1.5 text-xs font-semibold text-bg-base disabled:opacity-50"
            data-testid="provider-submit"
          >
            {busy ? 'Connecting…' : 'Connect'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ── API keys ───────────────────────────────────────────────────────────────

interface ApiKeyRow {
  key_id: string;
  key_preview: string;
  label: string;
  scopes: string[];
  rate_limit_rpm: number;
  created_at: string;
  last_used_at: string | null;
  active: boolean;
}

function ApiKeysPanel(): JSX.Element {
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState<{ key: string; key_id: string } | null>(null);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const list = await shaktiApi.listApiKeys();
      setKeys(list.map((k) => ({ ...k, last_used_at: k.last_used_at ?? null })));
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <section className="shakti-card flex flex-col gap-4 p-6">
      <header className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">API keys</h2>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="rounded-sm bg-fg-primary px-3 py-1.5 text-xs font-semibold text-bg-base"
          data-testid="create-key-button"
        >
          + Create key
        </button>
      </header>

      {loading ? (
        <p className="text-xs text-fg-muted">Loading…</p>
      ) : error ? (
        <ErrorBanner message={error} />
      ) : keys.length === 0 ? (
        <p className="text-sm text-fg-secondary">No API keys yet.</p>
      ) : (
        <table className="w-full text-xs" data-testid="api-keys-table">
          <thead>
            <tr className="text-left text-2xs uppercase tracking-wider text-fg-muted">
              <th className="py-2">Label</th>
              <th>Preview</th>
              <th>Scopes</th>
              <th>Created</th>
              <th>Last used</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.key_id} className="border-t border-border-subtle">
                <td className="py-2 font-semibold">{k.label}</td>
                <td className="shakti-mono text-2xs text-fg-muted">{k.key_preview}</td>
                <td className="text-2xs">{k.scopes.join(', ') || '—'}</td>
                <td className="text-2xs text-fg-muted">{formatDate(k.created_at)}</td>
                <td className="text-2xs text-fg-muted">
                  {k.last_used_at ? formatDate(k.last_used_at) : 'never'}
                </td>
                <td className="text-right">
                  <button
                    type="button"
                    disabled={!k.active}
                    onClick={async () => {
                      if (!window.confirm(`Revoke "${k.label}"?`)) return;
                      try {
                        await shaktiApi.revokeApiKey(k.key_id);
                        await refresh();
                      } catch (e) {
                        setError(friendlyError(e));
                      }
                    }}
                    className="text-2xs text-red-300 hover:text-red-200 disabled:opacity-40"
                  >
                    {k.active ? 'Revoke' : 'revoked'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showCreate ? (
        <ModalShell title="Create API key" onClose={() => setShowCreate(false)}>
          <CreateKeyForm
            onCreated={async (generated) => {
              setNewKey({ key: generated.key, key_id: generated.key_id });
              setShowCreate(false);
              await refresh();
            }}
          />
        </ModalShell>
      ) : null}

      {newKey ? (
        <ModalShell
          title="Save this key now"
          onClose={() => setNewKey(null)}
        >
          <p className="text-xs text-fg-muted">
            This is the only time the raw key is shown. Copy it and store it somewhere safe.
          </p>
          <pre
            className="shakti-mono my-3 overflow-x-auto rounded-sm border border-border-subtle bg-bg-surface p-3 text-xs text-fg-primary"
            data-testid="new-key-reveal"
          >
            {newKey.key}
          </pre>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(newKey.key);
              }}
              className="rounded-sm border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs"
            >
              Copy
            </button>
            <button
              type="button"
              onClick={() => setNewKey(null)}
              className="ml-2 rounded-sm bg-fg-primary px-3 py-1.5 text-xs font-semibold text-bg-base"
            >
              I've saved it
            </button>
          </div>
        </ModalShell>
      ) : null}
    </section>
  );
}

function CreateKeyForm({
  onCreated,
}: {
  onCreated: (g: { key: string; key_id: string }) => void;
}): JSX.Element {
  const [label, setLabel] = useState('Dashboard session');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const g = await shaktiApi.createApiKey({ label: label.trim(), scopes: ['read', 'write'] });
      onCreated({ key: g.key, key_id: g.key_id });
    } catch (e) {
      setError(friendlyError(e));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-xs">
        Label
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
          disabled={busy}
          required
          data-testid="new-key-label-input"
        />
      </label>
      {error ? <ErrorBanner message={error} /> : null}
      <div className="mt-2 flex justify-end">
        <button
          type="submit"
          disabled={busy || label.trim().length < 2}
          className="rounded-sm bg-fg-primary px-3 py-1.5 text-xs font-semibold text-bg-base disabled:opacity-50"
          data-testid="create-key-submit"
        >
          {busy ? 'Minting…' : 'Create key'}
        </button>
      </div>
    </form>
  );
}

// ── Shared UI ──────────────────────────────────────────────────────────────

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: JSX.Element | JSX.Element[];
  onClose: () => void;
}): JSX.Element {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="shakti-card w-full max-w-md bg-bg-base p-6">
        <header className="mb-4 flex items-start justify-between">
          <h3 id="modal-title" className="text-lg font-semibold">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-fg-muted hover:text-fg-primary"
          >
            ×
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }): JSX.Element {
  return (
    <div
      role="alert"
      className="rounded-sm border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200"
    >
      {message}
    </div>
  );
}

function friendlyError(e: unknown): string {
  if (e instanceof ApiError) {
    return `${e.body?.error ?? e.message} (${e.status})`;
  }
  if (e instanceof Error) return e.message;
  return 'Unexpected error';
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// ── K4.4 (round-5) — VCS credentials panel ────────────────────────────────

interface VcsCredential {
  id: string;
  project_id: string;
  provider: string;
  base_url: string;
  label: string;
  scopes: string[];
  created_at: string;
  rotated_at: string | null;
  expires_at: string | null;
}

function VcsCredentialsPanel(): JSX.Element {
  const [credentials, setCredentials] = useState<VcsCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const list = await shaktiApi.listVcsCredentials();
      setCredentials(list);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <section className="shakti-card flex flex-col gap-4 p-6">
      <header className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">VCS credentials</h2>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="rounded-sm bg-fg-primary px-3 py-1.5 text-xs font-semibold text-bg-base"
          data-testid="add-vcs-button"
        >
          + Connect repository
        </button>
      </header>

      <p className="text-xs text-fg-muted">
        Personal access tokens are encrypted at rest (AES-256-GCM) and used by Shakti to push
        generated code to your GitHub or GitLab repository after approval. Tokens never leave
        your cluster.
      </p>

      {loading ? (
        <p className="text-xs text-fg-muted">Loading…</p>
      ) : error ? (
        <ErrorBanner message={error} />
      ) : credentials.length === 0 ? (
        <p className="text-sm text-fg-secondary">
          No credentials connected. Add one to push generated code to your repo.
        </p>
      ) : (
        <table className="w-full text-xs" data-testid="vcs-credentials-table">
          <thead>
            <tr className="text-left text-2xs uppercase tracking-wider text-fg-muted">
              <th className="py-2">Provider</th>
              <th>Label</th>
              <th>Base URL</th>
              <th>Project</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {credentials.map((c) => (
              <tr key={c.id} className="border-t border-border-subtle">
                <td className="py-2 font-semibold">{c.provider}</td>
                <td>{c.label}</td>
                <td className="shakti-mono text-2xs">{c.base_url}</td>
                <td className="shakti-mono text-2xs text-fg-muted">
                  {c.project_id.slice(0, 8)}
                </td>
                <td className="text-2xs text-fg-muted">{formatDate(c.created_at)}</td>
                <td className="text-right">
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm(`Revoke "${c.label}"?`)) return;
                      try {
                        await shaktiApi.deleteVcsCredential(c.id);
                        await refresh();
                      } catch (e) {
                        setError(friendlyError(e));
                      }
                    }}
                    className="text-2xs text-red-300 hover:text-red-200"
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd ? (
        <AddVcsCredentialModal
          onClose={() => setShowAdd(false)}
          onAdded={async () => {
            setShowAdd(false);
            await refresh();
          }}
        />
      ) : null}
    </section>
  );
}

function AddVcsCredentialModal({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  onAdded: () => void;
}): JSX.Element {
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const projects = useProjectStore((s) => s.projects);
  const [projectId, setProjectId] = useState<string>(activeProjectId ?? projects[0]?.id ?? '');
  const [provider, setProvider] = useState<'github' | 'gitlab'>('github');
  const [pat, setPat] = useState('');
  const [label, setLabel] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!projectId) {
      setError('Select a project first.');
      return;
    }
    setBusy(true);
    try {
      await shaktiApi.createVcsCredential({
        project_id: projectId,
        provider,
        pat: pat.trim(),
        base_url: baseUrl.trim() || null,
        label: label.trim() || `${provider} personal`,
      });
      onAdded();
    } catch (e) {
      setError(friendlyError(e));
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Connect ${provider === 'github' ? 'GitHub' : 'GitLab'}`} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-xs">
          Project
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            disabled={busy}
            required
            data-testid="vcs-project-select"
          >
            <option value="">(choose a project)</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Provider
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value as 'github' | 'gitlab')}
            className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            disabled={busy}
            data-testid="vcs-provider-select"
          >
            <option value="github">GitHub</option>
            <option value="gitlab">GitLab</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Personal access token
          <input
            type="password"
            value={pat}
            onChange={(e) => setPat(e.target.value)}
            placeholder={provider === 'github' ? 'ghp_…' : 'glpat-…'}
            required
            disabled={busy}
            className="shakti-mono rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            data-testid="vcs-pat-input"
          />
          <span className="text-2xs text-fg-muted">
            Scopes needed: {provider === 'github' ? 'repo, write:repo_hook' : 'api, write_repository'}
          </span>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Label
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Company GitHub"
            disabled={busy}
            className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Base URL (for self-hosted / GHES)
          <input
            type="url"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder={
              provider === 'github'
                ? 'https://api.github.com (default)'
                : 'https://gitlab.com/api/v4 (default)'
            }
            disabled={busy}
            className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
          />
        </label>
        {error ? <ErrorBanner message={error} /> : null}
        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-sm border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || pat.trim().length < 8 || !projectId}
            className="rounded-sm bg-fg-primary px-3 py-1.5 text-xs font-semibold text-bg-base disabled:opacity-50"
            data-testid="vcs-submit"
          >
            {busy ? 'Saving…' : 'Save credential'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// #119 (round-6 close-out) — SCIM / SAML / DSAR panels.
// ═══════════════════════════════════════════════════════════════════════════

function ScimPanel(): JSX.Element {
  const [tokens, setTokens] = useState<Array<{
    id: string;
    label: string;
    created_at: string;
    last_used_at: string | null;
    revoked_at: string | null;
    token_preview: string;
  }>>([]);
  const [label, setLabel] = useState('');
  const [revealed, setRevealed] = useState<{ id: string; raw_token: string; label: string } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    try {
      const list = await shaktiApi.listScimTokens();
      setTokens(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to list tokens');
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function onCreate(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!label.trim()) return;
    setError(null);
    try {
      const t = await shaktiApi.createScimToken({ label: label.trim() });
      setRevealed(t);
      setLabel('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to create token');
    }
  }

  async function onRevoke(id: string): Promise<void> {
    try {
      await shaktiApi.revokeScimToken(id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to revoke token');
    }
  }

  const scimBaseUrl =
    typeof window !== 'undefined' ? `${window.location.origin}/scim/v2` : '/scim/v2';

  return (
    <section className="flex flex-col gap-4" aria-label="SCIM tokens">
      <div className="shakti-card flex flex-col gap-2">
        <h2 className="text-lg font-semibold">SCIM 2.0 provisioning</h2>
        <p className="text-sm text-fg-secondary">
          Your IdP (Okta, Entra, OneLogin, Google Workspace) points at this URL and
          authenticates with the bearer token you mint below. Revoking a token
          immediately blocks further SCIM writes.
        </p>
        <div className="shakti-mono text-xs break-all">
          <span className="text-fg-muted">Base URL: </span>
          {scimBaseUrl}
        </div>
      </div>

      <form className="shakti-card flex flex-col gap-3" onSubmit={onCreate}>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-fg-muted">Token label</span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Okta production"
            className="rounded-sm border border-border-subtle bg-bg-surface p-2 text-sm"
          />
        </label>
        <button
          type="submit"
          className="self-start rounded-sm bg-fg-primary px-3 py-1.5 text-xs font-semibold text-bg-base"
        >
          Mint new token
        </button>
      </form>

      {revealed && (
        <div
          role="status"
          className="shakti-card border border-status-warning/50 bg-status-warning/10"
        >
          <div className="text-xs font-semibold text-status-warning">
            Save this now — it's shown exactly once.
          </div>
          <div className="mt-2 shakti-mono text-sm break-all">{revealed.raw_token}</div>
          <button
            type="button"
            onClick={() => setRevealed(null)}
            className="mt-3 rounded-sm border border-border-subtle px-2 py-1 text-xs"
          >
            Dismiss
          </button>
        </div>
      )}

      {error && (
        <div role="alert" className="text-xs text-status-error">
          {error}
        </div>
      )}

      <table className="w-full text-xs">
        <thead className="text-left text-2xs uppercase tracking-wider text-fg-muted">
          <tr>
            <th className="py-2">Label</th>
            <th>Preview</th>
            <th>Created</th>
            <th>Last used</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {tokens.map((t) => (
            <tr key={t.id} className="border-t border-border-subtle">
              <td className="py-2">{t.label}</td>
              <td className="shakti-mono text-2xs text-fg-muted">{t.token_preview}…</td>
              <td className="text-2xs text-fg-muted">{new Date(t.created_at).toLocaleString()}</td>
              <td className="text-2xs text-fg-muted">
                {t.last_used_at ? new Date(t.last_used_at).toLocaleString() : '—'}
              </td>
              <td>
                {t.revoked_at ? (
                  <span className="text-status-error">revoked</span>
                ) : (
                  <span className="text-status-success">active</span>
                )}
              </td>
              <td>
                {!t.revoked_at && (
                  <button
                    type="button"
                    onClick={() => void onRevoke(t.id)}
                    className="rounded-sm border border-status-error/40 px-2 py-1 text-2xs text-status-error"
                  >
                    Revoke
                  </button>
                )}
              </td>
            </tr>
          ))}
          {tokens.length === 0 && (
            <tr>
              <td colSpan={6} className="py-3 text-center text-fg-muted">
                No SCIM tokens yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

function SamlPanel(): JSX.Element {
  const [metadataXml, setMetadataXml] = useState('');
  const [idpResult, setIdpResult] = useState<{
    entity_id: string;
    sso_url: string;
    slo_url: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onUpload(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!metadataXml.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await shaktiApi.uploadSamlIdpMetadata({ metadata_xml: metadataXml });
      setIdpResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to upload IdP metadata');
    } finally {
      setSubmitting(false);
    }
  }

  const spMetadataUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/v1/auth/saml/metadata.xml`
      : '/v1/auth/saml/metadata.xml';

  return (
    <section className="flex flex-col gap-4" aria-label="SAML SSO">
      <div className="shakti-card flex flex-col gap-2">
        <h2 className="text-lg font-semibold">SAML 2.0 SSO</h2>
        <p className="text-sm text-fg-secondary">
          Upload your IdP's metadata XML to connect Okta / Entra / PingFederate /
          ADFS / Google. Shakti's Service Provider metadata is available at the
          URL below — hand it to your IdP admin.
        </p>
        <div className="shakti-mono text-xs break-all">
          <span className="text-fg-muted">SP metadata URL: </span>
          <a href={spMetadataUrl} className="text-accent underline">
            {spMetadataUrl}
          </a>
        </div>
      </div>

      <form className="shakti-card flex flex-col gap-3" onSubmit={onUpload}>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-fg-muted">Paste IdP metadata XML</span>
          <textarea
            rows={10}
            value={metadataXml}
            onChange={(e) => setMetadataXml(e.target.value)}
            placeholder='<?xml version="1.0"?>&#10;<md:EntityDescriptor …>&#10;  …&#10;</md:EntityDescriptor>'
            className="rounded-sm border border-border-subtle bg-bg-surface p-2 shakti-mono text-2xs"
          />
        </label>
        <button
          type="submit"
          disabled={submitting}
          className="self-start rounded-sm bg-fg-primary px-3 py-1.5 text-xs font-semibold text-bg-base disabled:opacity-50"
        >
          {submitting ? 'Uploading…' : 'Save IdP metadata'}
        </button>
      </form>

      {error && (
        <div role="alert" className="text-xs text-status-error">
          {error}
        </div>
      )}

      {idpResult && (
        <div className="shakti-card flex flex-col gap-2 border border-status-success/40">
          <div className="text-xs font-semibold text-status-success">
            ✓ IdP configured
          </div>
          <dl className="text-xs">
            <dt className="mt-2 text-fg-muted">Entity ID</dt>
            <dd className="shakti-mono break-all">{idpResult.entity_id}</dd>
            <dt className="mt-2 text-fg-muted">SSO URL</dt>
            <dd className="shakti-mono break-all">{idpResult.sso_url}</dd>
            {idpResult.slo_url && (
              <>
                <dt className="mt-2 text-fg-muted">SLO URL</dt>
                <dd className="shakti-mono break-all">{idpResult.slo_url}</dd>
              </>
            )}
          </dl>
        </div>
      )}
    </section>
  );
}

function DsarPanel(): JSX.Element {
  const [email, setEmail] = useState('');
  const [action, setAction] = useState<'export' | 'erase'>('export');
  const [result, setResult] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const res =
        action === 'export'
          ? await shaktiApi.dsarExport(email.trim())
          : await shaktiApi.dsarErase(email.trim());
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'DSAR request failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="flex flex-col gap-4" aria-label="DSAR operations">
      <div className="shakti-card flex flex-col gap-2">
        <h2 className="text-lg font-semibold">GDPR DSAR</h2>
        <p className="text-sm text-fg-secondary">
          <strong>Export</strong> returns every record Shakti holds about the subject
          across users / emails / audit / model-usage tables — Article 15.
          <br />
          <strong>Erase</strong> soft-deletes the subject + schedules a 30-day
          hard delete; the Merkle audit chain stays verifiable via user_id
          nullification — Article 17.
        </p>
      </div>

      <form className="shakti-card flex flex-col gap-3" onSubmit={onSubmit}>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-fg-muted">Subject email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="subject@example.com"
            className="rounded-sm border border-border-subtle bg-bg-surface p-2 text-sm"
            required
          />
        </label>
        <div className="flex gap-4 text-xs">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="dsar-action"
              checked={action === 'export'}
              onChange={() => setAction('export')}
            />
            Generate subject report (Article 15)
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="dsar-action"
              checked={action === 'erase'}
              onChange={() => setAction('erase')}
            />
            Request erasure (Article 17)
          </label>
        </div>
        <button
          type="submit"
          disabled={submitting}
          className={`self-start rounded-sm px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${
            action === 'erase'
              ? 'bg-status-error text-bg-base'
              : 'bg-fg-primary text-bg-base'
          }`}
        >
          {submitting ? 'Running…' : action === 'erase' ? 'Request erasure' : 'Generate report'}
        </button>
      </form>

      {error && (
        <div role="alert" className="text-xs text-status-error">
          {error}
        </div>
      )}

      {result !== null && (
        <div className="shakti-card">
          <div className="text-xs font-semibold text-fg-primary">Response</div>
          <pre className="mt-2 max-h-96 overflow-auto shakti-mono text-2xs">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
    </section>
  );
}
